import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { format } from 'date-fns';
import { MdErrorOutline } from "react-icons/md";
import Timeline from 'react-calendar-timeline';
import moment from 'moment';
import 'react-calendar-timeline/style.css';
import 'moment/locale/ko';
import { compatApi } from '../../utils/Intercepter';

moment.locale('ko'); // 한국어 로케일 적용

function ReadProjectList() {
  const storedUser = sessionStorage.getItem('user');
  const user = storedUser && JSON.parse(storedUser);
  const organizationCode = user?.organizationCode || '';

  const location = useLocation();
  const project = location.state || {}; // state 값이 없으면 빈 객체로 설정

  // 포커스를 위해 REF세팅
  const employee = useRef(null);
  const startDate = useRef(null);
  const endDate = useRef(null);

  //에러코드 세팅
  const [employeeErr, setEmployeeErr] = useState("");
  const [startDateErr, setStartDateErr] = useState("");
  const [endDateErr, setEndDateErr] = useState("");

  const [projectAssignment, setProjectAssignment] = useState([]);
  const [projectAssignmentInfo, setProjectAssignmentInfo] = useState({
    projectInfoId: '',
    projectId: '',
    userId: '',
    task: '',
    startDate: '',
    endDate: '',
  });

  const [userList, setUserList] = useState([]);


  const [currentPage, setCurrentPage] = useState(1); // 현재 페이지
  const itemsPerPage = 5; // 페이지당 데이터 수
  const [totalPages] = useState(1); // 전체 페이지 수
  const maxPageButtons = 3; // 한 번에 표시할 최대 페이지 버튼 수

  const [showModal, setShowModal] = useState(false);

  const [chartKey, setChartKey] = useState(0);
  
  const reloadGanttChart = () => {
    setChartKey((prevKey) => prevKey + 1); // key 값 변경
  };


  // Open modal
  const openModal = () => {
    setShowModal(true);
  };

  // Close modal
  const closeModal = () => {
    setShowModal(false);
  };

  // Fetch data function
  const fetchData = async () => {
    try {
      const response = await compatApi.post('/user/readEmployeeList', organizationCode);
      const data = await response.json();
      // 가입 반려자는 조직 구성원이 아니므로 인원 선택 목록에서 제외
      setUserList((data || []).filter(u => !u.rejected));
    } catch (error) {
      console.log(error);
      alert('오류가 발생했습니다.');
    }
  };

  // 데이터 로드
  const fetchChartData = async () => {
    try {
      const response = await compatApi.post('/project/readProjectAssignmentList', project.projectId);
      const data = await response.json();
      setProjectAssignment(data);
    } catch (error) {
      console.log(error);
      alert('오류가 발생했습니다.');
    }
  };

  useEffect(() => {
    fetchChartData();
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps


  useEffect(() => {
    // Gantt 차트 헤더 스타일 변경
    const headerRoot = document.querySelector('.react-calendar-timeline .rct-header-root');
    if (headerRoot) {
      headerRoot.style.background = '#007bff'; // 밝은 파란색
      headerRoot.style.borderBottom = '1px solid #1976D2'; // 진한 파란색 테두리
    }
  }, [chartKey]); // 페이지 로드 후 한 번만 실행


  const currentYear = new Date().getFullYear();
  const defaultStart = new Date(currentYear, 0, 1);
  const defaultEnd = new Date(currentYear, 11, 31);

  // 직원 목록을 기반으로 그룹 생성 (중복 제거)
  const names = [...new Set(projectAssignment.map((project) => project.userName))];

  // 직원별 그룹 배열
  const groups = names.map((userName, index) => ({
    id: index + 1, // 그룹 ID (1부터 시작)
    title: userName, // 직원 이름
  }));

  // 프로젝트 데이터를 그룹 ID에 맞게 매핑
  const items = projectAssignment.map((project) => {
    const groupId = groups.find((g) => g.title === project.userName)?.id; // 고객사의 그룹 ID 찾기

    return {
      key:{chartKey},
      id: project.projectInfoId,
      group: groupId,
      title: project.task,
      start_time: moment(project.startDate).valueOf(),
      end_time: moment(project.endDate).valueOf(),
      canMove: false,
      canResize: false,
      itemProps: {
        style: {
          background: '#2196F3',
          color: 'white',
          borderRadius: '5px',
          border: '1px solid #1976D2',
        },
      },
    };
  });

  // 현재 페이지에 표시할 데이터 계산
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = projectAssignment.slice(startIndex, endIndex);

  // 페이지 버튼 그룹 계산
  const startPage = Math.floor((currentPage - 1) / maxPageButtons) * maxPageButtons + 1;
  const endPage = Math.min(startPage + maxPageButtons - 1, totalPages);

  // 페이지 변경 핸들러
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // 다음/이전 버튼 핸들러
  const handleNextGroup = () => {
    if (endPage < totalPages) {
      setCurrentPage(endPage + 1);
    }
  };

  const handlePrevGroup = () => {
    if (startPage > 1) {
      setCurrentPage(startPage - 1);
    }
  };

  function handleInputChange(event) {
    const { name, value, type, checked } = event.target;

    setProjectAssignmentInfo((prevState) => ({
      ...prevState,
      [name]: type === 'checkbox' ? checked : value,
    }));
  }
  const handleEditClick = (projectAssignment) => {
    setProjectAssignmentInfo(projectAssignment);
    openModal(); // 모달 열기
  };

  function handleCreateClick() {
    setProjectAssignmentInfo({
      projectInfoId: '',
      projectId: '',
      userId: '',
      task: '',
      startDate: '',
      endDate: '',
    });
    openModal(); // 모달 열기
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    projectAssignmentInfo.projectId = project.projectId;

    if (!projectAssignmentInfo.userId) {
      setEmployeeErr("인력을 선택해 주세요.");
      employee.current.focus();
      return;
    } else {
      setEmployeeErr("");
    }

    if (!projectAssignmentInfo.startDate) {
      setStartDateErr("시작일을 입력해 주세요.");
      startDate.current.focus();
      return;
    } else {
      setStartDateErr("");
    }

    if (!projectAssignmentInfo.endDate) {
      setEndDateErr("종료일을 입력해 주세요.");
      endDate.current.focus();
      return;
    } else {
      setEndDateErr("");
    }
    
    try {
      const response = await compatApi.post('/project/saveProjectAssignment', projectAssignmentInfo);

      if (response.ok) {
        const result = await response.text();
        alert(result);

        // 리스트 새로 불러오기
        await fetchChartData();
        await fetchData(); // 기존의 fetchData 함수 호출
        reloadGanttChart();
        closeModal(); // 모달 닫기
      } else {
        alert('요청 처리 중 오류가 발생했습니다.');
      }
    } catch (error) {
      alert(`에러 발생: ${error.message}`);
    }
  };

  return (
    <div style={{ padding: '20px', backgroundColor: '#f0f8ff' }}>
      {/* Modal */}
      {showModal && (
        <div className="modal fade show" tabIndex="-1" style={{ display: 'block' }} aria-hidden="false">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">인력 등록/수정</h5>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label htmlFor="userId" className="form-label">인력</label>
                  <select id="userId" name="userId" value={projectAssignmentInfo.userId} className="form-control" onChange={handleInputChange} ref={employee}>
                    <option value="">--- 선택 ---</option>
                    {
                      userList.map((user) => (
                        <option key={user.userId} value={user.userId}>
                          {user.userName} ({user.userId})
                        </option>
                      ))
                    }
                  </select>
                  {employeeErr && <div className="text-danger mt-2"><MdErrorOutline size={20} className="me-2" />{employeeErr}</div>}
                </div>
                <div className="mb-3">
                  <label htmlFor="task" className="form-label">역할</label>
                  <input id="task" name="task" value={projectAssignmentInfo.task} type="text" className="form-control" onChange={handleInputChange} />
                </div>
                <div className="mb-3">
                  <label htmlFor="startDate" className="form-label">시작일</label>
                  <input id="startDate" name="startDate" value={projectAssignmentInfo.startDate ? format(new Date(projectAssignmentInfo.startDate), 'yyyy-MM-dd') : ""} type="date" className="form-control" onChange={handleInputChange} ref={startDate} />
                  {startDateErr && <div className="text-danger mt-2"><MdErrorOutline size={20} className="me-2" />{startDateErr}</div>}
                </div>
                <div className="mb-3">
                  <label htmlFor="endDate" className="form-label">종료일</label>
                  <input id="endDate" name="endDate" value={projectAssignmentInfo.endDate ? format(new Date(projectAssignmentInfo.endDate), 'yyyy-MM-dd') : ""} type="date" className="form-control" onChange={handleInputChange} ref={endDate} />
                  {endDateErr && <div className="text-danger mt-2"><MdErrorOutline size={20} className="me-2" />{endDateErr}</div>}
                </div>
              </div>
              <div className="modal-footer">
                <div className="button-container">
                  <button className="btn btn-primary" onClick={handleSubmit}>저장</button>
                  <button onClick={closeModal} className="btn btn-secondary">취소</button>
                </div>

              </div>
            </div>
          </div>
        </div>
      )}
      <div style={{ marginBottom: '20px' }}>
        <div style={{ display: "flex", alignItems: "center", paddingBottom: "10px" }}>
          <h2>프로젝트 : {project.projectName}</h2>
        </div>

        <Timeline
          groups={groups}
          items={items}
          defaultTimeStart={defaultStart}
          defaultTimeEnd={defaultEnd}
          itemHeight={30}
          lineHeight={40}
          stackItems={true}
          canMove={false}
          canResize={false}
          canChangeGroup={false}
          onItemSelect={() => { }}
          timeSteps={{
            year: 1,
            month: 1,
            day: 1,
            hour: 1,
          }}
          headerLabelFormats={{
            yearLong: 'YYYY년',
            yearShort: 'YY년',
            monthShort: 'YYYY년 M월',
            monthMedium: 'YYYY년 M월',
            monthLong: 'YYYY년 M월',
            dayShort: 'M월 D일',
            dayLong: 'M월 D일',
          }}
        />
      </div>
      <div style={{ display: "flex", alignItems: "center", paddingBottom: "10px" }}>
        <h2>인력 관리</h2>
        <div style={{ marginLeft: "auto" }}>
          <button className="btn btn-primary" onClick={handleCreateClick}>인력 등록</button>
        </div>
      </div>

      <table className="list-table">
        <colgroup>
          <col style={{ width: '10%' }}></col>
          <col style={{ width: '50%' }}></col>
          <col style={{ width: '7%' }}></col>
          <col style={{ width: '7%' }}></col>
          <col style={{ width: '5%' }}></col>
        </colgroup>
        <thead>
          <tr>
            <th>이름</th>
            <th>역할</th>
            <th>시작일</th>
            <th>종료일</th>
            <th>관리</th>
          </tr>
        </thead>
        <tbody>
          {currentItems.map((projectAssignment) => (
            <tr key={projectAssignment.projectInfoId}>
              <td>{projectAssignment.userName}({projectAssignment.userId})</td>
              <td>{projectAssignment.task}</td>
              <td>{format(new Date(projectAssignment.startDate), 'yyyy-MM-dd')}</td>
              <td>{format(new Date(projectAssignment.endDate), 'yyyy-MM-dd')}</td>
              <td>
                <button className="btn btn-outline-primary" onClick={() => handleEditClick(projectAssignment)}>수정</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* 페이징 버튼 */}
      <div className="pagination">
        <button
          onClick={handlePrevGroup}
          disabled={startPage === 1}
          className="page-btn page-btn-prev"
        >
          &lt;
        </button>
        {
          Array.from({ length: endPage - startPage + 1 }, (_, index) => (
            <button
              key={startPage + index}
              onClick={() => handlePageChange(startPage + index)}
              className={`page-btn ${currentPage === startPage + index ? 'active' : ''}`}
            >
              {startPage + index}
            </button>
          ))
        }
        <button
          onClick={handleNextGroup}
          disabled={endPage === totalPages}
          className="page-btn page-btn-next"
        >
          &gt;
        </button>
      </div>
    </div>
  );
}

export default ReadProjectList;
